import { useLocation, useNavigate } from "react-router-dom";
import { footer } from "@/lib/content";
import { getLanguageFromPath, getLocalizedPath } from "@/lib/language";

const socials = [
  { icon: "ri-behance-line", href: "https://www.behance.net/iVision", label: "Behance" },
  { icon: "ri-linkedin-line", href: "https://www.linkedin.com/in/ivett-lakatos-42a7852bb/", label: "LinkedIn" },
  { icon: "ri-instagram-line", href: "https://www.instagram.com/ivision_design/", label: "Instagram" },
];

export default function Footer() {
  const location = useLocation();
  const navigate = useNavigate();
  const language = getLanguageFromPath(location.pathname);
  const copy = footer[language];

  const handleNavClick = (href: string) => {
    if (href.startsWith("#")) {
      const el = document.querySelector(href);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        return;
      }
      navigate(getLocalizedPath(language, href));
    }
  };

  return (
    <footer style={{ backgroundColor: "#003330", borderTop: "1px solid rgba(172,209,198,0.15)" }}>
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        {/* Top */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-10 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <a
              href="#home"
              onClick={(e) => { e.preventDefault(); handleNavClick("#home"); }}
              className="inline-block mb-4 cursor-pointer"
            >
              <img
                src="https://static.readdy.ai/image/6ecebf6e4231ef17c67ed0e84eadfb79/b427884c76c061c0d4381b501d2fe039.png"
                alt="iVision Design"
                className="h-48 w-auto object-contain"
              />
            </a>
            <p className="text-sm leading-relaxed max-w-xs mb-6" style={{ color: "rgba(172,209,198,0.55)" }}>
              {copy.text}
            </p>
            <div className="flex items-center gap-3">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  aria-label={s.label}
                  className="w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-300 cursor-pointer"
                  style={{ border: "1px solid rgba(172,209,198,0.25)", color: "rgba(172,209,198,0.6)" }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor = "#acd1c6";
                    (e.currentTarget as HTMLElement).style.color = "#004643";
                    (e.currentTarget as HTMLElement).style.borderColor = "#acd1c6";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
                    (e.currentTarget as HTMLElement).style.color = "rgba(172,209,198,0.6)";
                    (e.currentTarget as HTMLElement).style.borderColor = "rgba(172,209,198,0.25)";
                  }}
                >
                  <i className={`${s.icon} text-sm`} />
                </a>
              ))}
            </div>
          </div>

          {/* Links */}
          {copy.groups.map((group) => (
            <div key={group.title}>
              <div className="font-semibold text-sm mb-4" style={{ color: "#acd1c6" }}>{group.title}</div>
              <ul className="flex flex-col gap-2.5">
                {group.links.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      target={"external" in link && link.external ? "_blank" : undefined}
                      rel={"external" in link && link.external ? "noopener noreferrer" : undefined}
                      onClick={(e) => {
                        if (link.href.startsWith("#")) {
                          e.preventDefault();
                          handleNavClick(link.href);
                        }
                      }}
                      className="text-sm transition-colors cursor-pointer"
                      style={{ color: "rgba(172,209,198,0.5)" }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "#acd1c6"; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "rgba(172,209,198,0.5)"; }}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom */}
        <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-4" style={{ borderTop: "1px solid rgba(172,209,198,0.12)" }}>
          <p className="text-xs" style={{ color: "rgba(172,209,198,0.35)" }}>
            © {new Date().getFullYear()} iVision Design. {copy.rights}
          </p>
          <p className="text-xs" style={{ color: "rgba(172,209,198,0.35)" }}>
            {copy.credit} <strong style={{ color: "rgba(172,209,198,0.6)" }}>iVision Design</strong>
          </p>
        </div>
      </div>
    </footer>
  );
}
